import React from 'react';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody, Input } from '@repo/ui';
import type { ImageAsset } from '../api';
import { handleImageError, downloadFile } from '../utils';

// Standardized button styles
const BUTTON_STYLES = {
  standard: {
    variant: 'outline' as const,
    size: 'sm' as const,
  },
  destructive: {
    variant: 'destructive' as const,
    size: 'sm' as const,
  },
  ghost: {
    variant: 'ghost' as const,
    size: 'sm' as const,
  },
} as const;

interface ImageGalleryProps {
  images: ImageAsset[];
  isLoading: boolean;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onImagePreview: (imageId: string) => void;
  onImageDelete: (imageId: string) => void;
  isDeleting: boolean;
  isUploading: boolean;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  isLoading,
  onImageUpload,
  onImagePreview,
  onImageDelete,
  isDeleting,
  isUploading,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <Card className="w-full">
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-dark-50">
              Image Assets
            </h2>
            <p className="text-xtnd-dark-500 dark:text-xtnd-dark-300 text-sm">
              Upload and manage images for use in templates. Supported formats: JPEG, PNG, GIF,
              WebP, SVG
            </p>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-xtnd-blue"></div>
                <span className="ml-3 text-xtnd-dark-500 dark:text-xtnd-dark-50">
                  Loading images...
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {images.map(image => (
                  <Card key={image.imageId} className="hover:shadow-lg transition-shadow">
                    <CardBody className="p-4">
                      <div className="aspect-video bg-xtnd-dark-50 dark:bg-xtnd-dark-800 rounded-lg mb-3 overflow-hidden border-2 border-xtnd-light dark:border-xtnd-dark-600">
                        <Button
                          onClick={() => onImagePreview(image.imageId)}
                          type="button"
                          aria-label={`Preview ${image.originalName}`}
                          {...BUTTON_STYLES.ghost}
                          className="w-full h-full p-0 border-0 bg-transparent cursor-pointer
             hover:scale-105 transition-transform focus:outline-none
             focus:ring-2 focus:ring-xtnd-blue focus:ring-inset"
                        >
                          <img
                            src={image.previewUrl || ''}
                            alt={image.originalName || 'Image'}
                            className="w-full h-full object-contain"
                            onError={e => handleImageError(e, image, false)}
                          />
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <h3
                          className="font-medium text-xtnd-dark truncate"
                          title={image.originalName}
                        >
                          {image.originalName}
                        </h3>

                        <div className="flex items-center justify-between text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                          <span>
                            {image.dimensions?.width || '?'}x{image.dimensions?.height || '?'}
                          </span>
                          <span>{(image.size / 1024).toFixed(1)}KB</span>
                        </div>

                        {image.description && (
                          <p
                            className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200 truncate"
                            title={image.description}
                          >
                            {image.description}
                          </p>
                        )}

                        <div className="flex flex-col gap-2 pt-2">
                          <div className="flex items-center gap-1">
                            <Button
                              {...BUTTON_STYLES.standard}
                              onClick={() => onImagePreview(image.imageId)}
                              className="flex-1 text-xs"
                              leftIcon={<Icon name="HiEye" className="h-3 w-3" />}
                            >
                              Preview
                            </Button>

                            <Button
                              {...BUTTON_STYLES.standard}
                              onClick={() => downloadFile(image.downloadUrl || '', image.originalName)}
                              className="flex-1 text-xs"
                              leftIcon={<Icon name="HiDownload" className="h-3 w-3" />}
                            >
                              Download
                            </Button>
                          </div>

                          <Button
                            {...BUTTON_STYLES.destructive}
                            onClick={() => onImageDelete(image.imageId)}
                            disabled={isDeleting}
                            className="w-full text-xs"
                            leftIcon={<Icon name="HiTrash" className="h-3 w-3" />}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}

                {images.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <Icon
                      name="HiDocumentAdd"
                      className="h-12 w-12 text-xtnd-dark-800 dark:text-xtnd-dark-50 mx-auto mb-4"
                    />
                    <h3 className="text-lg font-medium text-xtnd-dark-900 dark:text-xtnd-dark-50 mb-2">
                      No images uploaded
                    </h3>
                    <p className="text-xtnd-dark-400 dark:text-xtnd-dark-300 mb-4 text-sm">
                      Upload your first image using the upload form above to get started with
                      template assets
                    </p>
                    <label className="inline-flex items-center px-4 py-2 bg-xtnd-blue text-white rounded-lg hover:bg-xtnd-dark transition-colors cursor-pointer dark:text-xtnd-dark-900 dark:hover:text-xtnd-white">
                      <Icon name="HiArrowUp" className="h-4 w-4 mr-2" />
                      Upload Image
                      <Input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={onImageUpload}
                        disabled={isUploading}
                      />
                    </label>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

